import { useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { learnerPaths } from '@app/routes/route-paths'
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Clock3,
  ClipboardList,
  RotateCcw,
  Save,
  XCircle,
} from 'lucide-react'
import { useSnackbar } from '@shared/ui/notification-context'
import { useTranslation } from 'react-i18next'
import { getYoutubeEmbedUrl } from '@shared/media/youtube/youtube-embed-url'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  LmsButton,
} from '@shared/ui'
import type {
  LearningWorkbookQuizItem,
} from '@services/firebase/learning'
import {
  addDailyActivityEntry,
  getElapsedDailyActivitySeconds,
  saveWorkbookQuizProgress,
} from '@services/firebase/self-study'
import { traceLearningPerformance } from '@shared/learning/learning-performance-trace'
import { useDashboardState } from '@features/dashboard'
import {
  LearningLessonShell,
  LearningLessonShellContent,
  LearningLessonShellFooter,
} from '@features/learning/shared/LearningLessonShell'
import { playLearningFeedbackAudio, useWarmLearningFeedbackAudio } from '@features/learning/shared/learning-feedback-audio'
import { LearningExerciseToolbar } from '@features/learning/shared/learning-exercise-toolbar'
import {
  formatLessonTitle,
  getAnswerOptionLabel,
  getScorableListeningQuizItems,
  hasScorableLearningAnswer,
  resolveListeningQuizPresentation,
  resolveListeningQuizQuestionTexts,
  resolveReaderLanguage,
  resolveListeningQuizPromptTitle,
} from '@features/learning/shared/learning-helpers'
import {
  LearningQuizTemplate,
  LearningTemplateExerciseResultBar,
  LearningTemplateIntroduction,
  LearningTemplatePromptMedia,
  LearningTemplateQuestionContent,
  LearningTemplateResultBar,
} from '@features/learning/shared/templates'
import type { LearningQuizAnswer } from '@features/learning/shared/templates'
import '../learning-responsive.css'

function ListeningQuizShellHeader({
  onBack,
  onReview,
  progress,
  reviewDisabled = false,
}: {
  readonly onBack: () => void
  readonly onReview?: () => void
  readonly progress?: number
  readonly reviewDisabled?: boolean
}) {
  const { t } = useTranslation('book')

  return (
    <LearningExerciseToolbar
      backSlot={
        <LmsButton
          aria-label={t('Back to learning')}
          className="learningListeningQuizHeaderIcon rounded-full"
          leadingIcon={<ArrowLeft aria-hidden="true" className="size-6" />}
          onClick={onBack}
          size="icon-lg"
          variant="ghost"
        >
          <span className="sr-only">{t('Back to learning')}</span>
        </LmsButton>
      }
      className="learningListeningQuizHeader"
      progress={progress ?? (onReview ? 100 : 0)}
      progressLabel={t('Listening quiz progress')}
      progressTrackClassName="learningListeningQuizProgress"
      progressValueClassName="learningListeningQuizProgressBar"
      rightSlot={
        onReview ? (
          <LmsButton
            aria-label={t('Review')}
            className="learningListeningQuizHeaderIcon rounded-full"
            disabled={reviewDisabled}
            leadingIcon={<ClipboardList aria-hidden="true" className="size-5" />}
            onClick={onReview}
            size="icon-lg"
            variant="ghost"
          >
            <span className="sr-only">{t('Review')}</span>
          </LmsButton>
        ) : null
      }
      rightSlotClassName="learningListeningQuizHeaderSideEnd"
      toolbarClassName="learningListeningQuizHeaderToolbar"
    />
  )
}

interface ListeningQuizAttempt {
  readonly id: string
  readonly result: 'correct' | 'incorrect'
}

type ListeningQuizStatusBadgeTone = 'neutral' | 'success' | 'warning'

function ListeningQuizStatusBadge({
  children,
  icon,
  tone = 'neutral',
}: {
  readonly children: ReactNode
  readonly icon?: ReactNode
  readonly tone?: ListeningQuizStatusBadgeTone
}) {
  return (
    <span
      className={`learningListeningQuizStatusBadge learningListeningQuizStatusBadge--${tone}`}
    >
      {icon}
      {children}
    </span>
  )
}

function ListeningQuizAlert({
  action,
  className,
  children,
  severity = 'info',
}: {
  readonly action?: ReactNode
  readonly className?: string
  readonly children: ReactNode
  readonly severity?: 'error' | 'info' | 'warning'
}) {
  return (
    <div
      className={`learningListeningQuizAlert learningListeningQuizAlert--${severity}${className ? ` ${className}` : ''}`}
      role={severity === 'error' ? 'alert' : 'status'}
    >
      <div className="learningListeningQuizAlertMessage">{children}</div>
      {action ? <div className="learningListeningQuizAlertAction">{action}</div> : null}
    </div>
  )
}

function MediaBlock({
  audio,
  audioAutoPlayKey,
  image,
  title,
  youtubeId,
}: {
  readonly audio?: string
  readonly audioAutoPlayKey?: string
  readonly image?: string
  readonly title?: string
  readonly youtubeId?: string
}) {
  if (!audio && !image && !youtubeId) {
    return null
  }

  return (
    <div className="learningListeningQuizMedia">
      <LearningTemplatePromptMedia
        audio={audio}
        audioAutoPlayKey={audioAutoPlayKey}
        audioAutoPlayOnLoad
        audioVariant="listeningQuiz"
        image={image}
        imageAlt={title ?? 'Listening quiz media'}
        showAdvancedAudioControls
      />

      {youtubeId ? (
        <div className="learningListeningQuizVideoSurface">
          <iframe
            src={getYoutubeEmbedUrl(youtubeId) ?? undefined}
            title={title ?? 'Listening quiz video'}
          />
        </div>
      ) : null}
    </div>
  )
}

function formatElapsedDuration(seconds: number) {
  const roundedSeconds = Math.max(0, Math.round(seconds))
  const minutes = Math.floor(roundedSeconds / 60)
  const remainingSeconds = roundedSeconds % 60

  if (minutes === 0) {
    return `${remainingSeconds}s`
  }

  return `${minutes}m ${remainingSeconds.toString().padStart(2, '0')}s`
}

function ListeningQuizPrompt({
  item,
  onSubmit,
  promptNumber,
}: {
  readonly item: LearningWorkbookQuizItem
  readonly onSubmit: (result: 'correct' | 'incorrect') => void
  readonly promptNumber: number
}) {
  const { i18n, t } = useTranslation('book')
  const locale = resolveReaderLanguage(i18n.language)
  const [selectedAnswerIndex, setSelectedAnswerIndex] = useState<number | null>(null)
  const [selectedResult, setSelectedResult] = useState<'correct' | 'incorrect' | null>(
    null,
  )

  const presentation = useMemo(
    () => resolveListeningQuizPresentation(item, locale),
    [item, locale],
  )
  const { answers, questions } = presentation

  const title = resolveListeningQuizPromptTitle(
    item,
    locale,
    promptNumber,
    t('Online Listening Quiz'),
  )
  const hasQuestionContent = questions.length > 0

  const commonAnswers = useMemo<LearningQuizAnswer[]>(
    () =>
      answers.map((answer, index) => ({
        audio: answer.audio,
        id: answer.id ?? `${item.id}-answer-${index}`,
        image: answer.image,
        isCorrect: hasScorableLearningAnswer(answer),
        label: getAnswerOptionLabel(answer, locale, `Option ${index + 1}`),
      })),
    [answers, item.id, locale],
  )

  if (presentation.kind === 'MULTIPLE_CHOICE') {
    return (
      <div className="learningListeningQuizPrompt">
        <div className="learningListeningQuizPromptHeader">
          <h1 className="learningListeningQuizHeading">{title}</h1>
          <p className="learningListeningQuizMutedText">
            {t('listeningQuiz.prompt.chooseAnswer')}
          </p>
        </div>

        <MediaBlock
          audio={item.audio}
          audioAutoPlayKey={item.id}
          image={item.image || item.featuredImage}
          title={title}
          youtubeId={item.youtubeId}
        />

        {hasQuestionContent ? (
          <LearningTemplateQuestionContent
            className="learningListeningQuizQuestions"
            questions={questions}
          />
        ) : null}

        <LearningQuizTemplate
          answers={commonAnswers}
          fullwidth={presentation.fullwidth}
          itemId={item.id}
          onAnswer={(answerIndex) => {
            const result = hasScorableLearningAnswer(answers[answerIndex])
              ? 'correct'
              : 'incorrect'
            setSelectedAnswerIndex(answerIndex)
            setSelectedResult(result)
            playLearningFeedbackAudio(result)
          }}
          oneThird={presentation.oneThird}
          result={selectedResult}
          selectedAnswerIndex={selectedAnswerIndex}
          sourceOwner="listening-quiz"
        />

        {selectedResult ? (
          <LearningTemplateExerciseResultBar
            onNext={() => onSubmit(selectedResult)}
            result={selectedResult}
            resultMessage={
              selectedResult === 'correct'
                ? t('listeningQuiz.result.correct')
                : t('listeningQuiz.result.incorrect')
            }
          />
        ) : (
          <LearningTemplateResultBar
            finished={false}
            label={t('Skip for now')}
            onNext={() => onSubmit('incorrect')}
            trailingIcon={<ArrowRight aria-hidden="true" className="size-4" />}
            variant="outline"
          />
        )}
      </div>
    )
  }

  return (
    <div className="learningListeningQuizPrompt">
      <div className="learningListeningQuizPromptHeader">
        <h1 className="learningListeningQuizHeading">{title}</h1>
      </div>

      {presentation.kind === 'CONTENT' ? (
        <MediaBlock
          audio={item.audio}
          audioAutoPlayKey={item.id}
          image={item.image || item.featuredImage}
          title={title}
          youtubeId={item.youtubeId}
        />
      ) : null}

      {hasQuestionContent ? (
        <LearningTemplateQuestionContent
          className="learningListeningQuizQuestions"
          questions={questions}
        />
      ) : null}

      {presentation.kind === 'UNAVAILABLE' ? (
        <ListeningQuizAlert>
          {t('listeningQuiz.prompt.unavailable')}
        </ListeningQuizAlert>
      ) : null}

      <LearningTemplateResultBar
        finished={false}
        label={t('Continue')}
        onNext={() => onSubmit('correct')}
        trailingIcon={<ArrowRight aria-hidden="true" className="size-4" />}
        variant="primary"
      />
    </div>
  )
}

type ListeningQuizPhase = 'introduction' | 'question' | 'completed'

function ListeningQuizSessionScreen({
  session,
}: {
  readonly session: NonNullable<
    ReturnType<typeof useDashboardState>['state']['listeningQuizSession']
  >
}) {
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const { i18n, t } = useTranslation('book')
  const { actions, state } = useDashboardState()
  const [quizItems] = useState<LearningWorkbookQuizItem[]>(
    session.onlineQuizItems,
  )
  const [stepIndex, setStepIndex] = useState(0)
  const [reviewOpen, setReviewOpen] = useState(false)
  const [attemptHistory, setAttemptHistory] = useState<ListeningQuizAttempt[]>([])
  const [reviewResults, setReviewResults] = useState<Record<string, 'correct' | 'incorrect'>>(
    {},
  )
  const [phase, setPhase] = useState<ListeningQuizPhase>('introduction')
  const [isSaving, setIsSaving] = useState(false)
  const [saveErrorMessage, setSaveErrorMessage] = useState<string | null>(null)
  const [timeSeconds, setTimeSeconds] = useState(0)

  const isFinished = phase === 'completed'
  const currentItem = quizItems[stepIndex] ?? null
  const originalItems = session.onlineQuizItems
  const scorableItems = useMemo(
    () => getScorableListeningQuizItems(originalItems),
    [originalItems],
  )
  const scorablePromptIds = useMemo(
    () => new Set(scorableItems.map((item) => item.id)),
    [scorableItems],
  )
  const correctScorableAttemptIds = useMemo(() => {
    const ids = new Set<string>()

    attemptHistory.forEach((attempt) => {
      if (attempt.result === 'correct' && scorablePromptIds.has(attempt.id)) {
        ids.add(attempt.id)
      }
    })

    return ids
  }, [attemptHistory, scorablePromptIds])
  const correctCount = correctScorableAttemptIds.size
  const incorrectCount = attemptHistory.filter(
    (attempt) => attempt.result === 'incorrect',
  ).length
  const hasScorablePrompts = scorableItems.length > 0
  const ratingValue = hasScorablePrompts
    ? Number(((correctCount / scorableItems.length) * 5).toFixed(1))
    : 0
  const rating = ratingValue.toFixed(1)
  const progress =
    phase === 'introduction'
      ? 0
      : quizItems.length > 0
        ? ((stepIndex + 1) / quizItems.length) * 100
        : 0

  const handleReturnToLearning = () => {
    actions.setListeningQuizSession(null)
    navigate(learnerPaths.learning)
  }

  const handleSubmitResult = (result: 'correct' | 'incorrect') => {
    if (phase !== 'question' || !currentItem) {
      return
    }

    setAttemptHistory((previous) => [
      ...previous,
      { id: currentItem.id, result },
    ])
    setReviewResults((previous) => ({
      ...previous,
      [currentItem.id]: result,
    }))

    const willContinue = stepIndex < quizItems.length - 1

    if (willContinue) {
      setStepIndex((previous) => previous + 1)
      return
    }

    setPhase('completed')
    setTimeSeconds(
      getElapsedDailyActivitySeconds({ startedAt: session.createdAt }),
    )
  }

  const handleSaveAndExit = async () => {
    if (!session) {
      return
    }

    try {
      if (!session.bookId) {
        setSaveErrorMessage(
          'Cannot save quiz completion because this lesson is not linked to a learning program.',
        )
        return
      }

      const bookId = session.bookId
      const lessonId = session.lesson.id

      setSaveErrorMessage(null)
      setIsSaving(true)
      const progressWorkbookLessons = await traceLearningPerformance({
        event: 'listeningQuiz.workbookProgress.save',
        getDetails: (progressEntries) => ({
          correctPromptCount: correctCount,
          hasBookId: Boolean(bookId),
          hasLessonId: Boolean(lessonId),
          retryPromptCount: incorrectCount,
          savedProgressEntries: progressEntries.length,
          scorablePromptCount: scorableItems.length,
        }),
        task: () => saveWorkbookQuizProgress({
          bookId,
          lessonId,
          rating,
        }),
      })
      const completionSeconds =
        timeSeconds > 0
          ? timeSeconds
          : getElapsedDailyActivitySeconds({ startedAt: session.createdAt })

      setTimeSeconds(completionSeconds)
      const activity = await addDailyActivityEntry({
        seconds: completionSeconds,
      })

      actions.mergeMyUser({
        daily_activities: (() => {
          const existingActivities = state.myUser?.daily_activities ?? []
          const existingIndex = existingActivities.findIndex(
            (entry) => entry.id === activity.id,
          )

          if (existingIndex >= 0) {
            const nextActivities = [...existingActivities]
            nextActivities[existingIndex] = activity
            return nextActivities
          }

          return [...existingActivities, activity]
        })(),
        progress_workbook_lessons: progressWorkbookLessons,
      })
      setSaveErrorMessage(null)
      enqueueSnackbar(t('Updated successfully', { ns: 'translation' }), {
        variant: 'success',
      })
      handleReturnToLearning()
    } catch {
      setSaveErrorMessage(
        t('Failed to save quiz completion. Review your connection and try again.'),
      )
      enqueueSnackbar(t('Updated failed', { ns: 'translation' }), {
        variant: 'error',
      })
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <LearningLessonShell className="learningResponsiveScreen learningListeningQuizScreen learningListeningQuizShell">
      <ListeningQuizShellHeader
        onBack={handleReturnToLearning}
        onReview={() => {
          setReviewOpen(true)
        }}
        progress={!isFinished ? progress : undefined}
        reviewDisabled={phase !== 'question' || stepIndex === 0}
      />

      <LearningLessonShellContent
        className="learningListeningQuizContent"
        pageWrapClassName="learningListeningQuizPageWrap"
        viewportClassName="learningListeningQuizViewport"
      >
        <div
          className="learningListeningQuizLesson"
          data-qa={`learning-listening-quiz-phase-${phase}`}
        >
          {phase === 'introduction' ? (
            <LearningTemplateIntroduction
              content={
                <div className="learningListeningQuizIntroCopy">
                  <h1 className="learningListeningQuizHeading">
                    {formatLessonTitle(
                      t,
                      session.lesson,
                      resolveReaderLanguage(i18n.language),
                    )}
                  </h1>
                  <p className="learningListeningQuizMutedText">
                    {t('Take this quiz to test your listening skills')}
                  </p>
                </div>
              }
              imageSrc="/images/learning-introduce.webp"
              onContinue={() => setPhase('question')}
            />
          ) : null}

          {phase === 'question' && currentItem ? (
            <ListeningQuizPrompt
              item={currentItem}
              onSubmit={handleSubmitResult}
              promptNumber={stepIndex + 1}
              key={currentItem.id}
            />
          ) : null}

          {phase === 'question' && !currentItem ? (
            <ListeningQuizAlert
              action={
                <LmsButton
                  leadingIcon={<ArrowLeft aria-hidden="true" className="size-4" />}
                  onClick={handleReturnToLearning}
                  size="sm"
                  variant="outline"
                >
                  Back
                </LmsButton>
              }
              className="learningListeningQuizFallback"
              severity="warning"
            >
              No listening quiz prompts are available for this lesson right now.
            </ListeningQuizAlert>
          ) : null}

          {isFinished ? (
            <section
              aria-labelledby="learning-listening-quiz-completion-title"
              className="learningListeningQuizCompletion"
            >
              <div className="learningListeningQuizCompletionContent">
                <div
                  className="learningListeningQuizCompletionHeader"
                >
                  <div className="learningListeningQuizCompletionCopy">
                    <h1
                      className="learningListeningQuizHeading"
                      id="learning-listening-quiz-completion-title"
                    >
                      {t('Listening quiz completed')}
                    </h1>
                  </div>
                  <ListeningQuizStatusBadge
                    icon={<Clock3 aria-hidden="true" className="size-4" />}
                  >
                    {formatElapsedDuration(timeSeconds)}
                  </ListeningQuizStatusBadge>
                </div>

                <div
                  className="learningListeningQuizCompletionMetrics"
                >
                  <div className="learningListeningQuizMetric">
                    <div className="learningListeningQuizMetricBody">
                      <span className="learningListeningQuizMetricLabel">
                        {t('Rating')}
                      </span>
                      <strong className="learningListeningQuizMetricValue">
                        {rating}/5
                      </strong>
                    </div>
                  </div>
                  <div className="learningListeningQuizMetric">
                    <div className="learningListeningQuizMetricBody">
                      <span className="learningListeningQuizMetricLabel">
                        {t('Correct prompts')}
                      </span>
                      <div className="learningListeningQuizMetricValue learningListeningQuizMetricValueWithIcon">
                        <CheckCircle2
                          aria-hidden="true"
                          className="size-6 learningListeningQuizMetricIcon learningListeningQuizMetricIcon--success"
                        />
                        <strong>
                          {correctCount}
                        </strong>
                      </div>
                    </div>
                  </div>
                  <div className="learningListeningQuizMetric">
                    <div className="learningListeningQuizMetricBody">
                      <span className="learningListeningQuizMetricLabel">
                        {t('Retry prompts')}
                      </span>
                      <div className="learningListeningQuizMetricValue learningListeningQuizMetricValueWithIcon">
                        <XCircle
                          aria-hidden="true"
                          className="size-6 learningListeningQuizMetricIcon learningListeningQuizMetricIcon--warning"
                        />
                        <strong>
                          {incorrectCount}
                        </strong>
                      </div>
                    </div>
                  </div>
                </div>

                {!hasScorablePrompts ? (
                  <ListeningQuizAlert severity="info">
                    {t(
                      'This listening quiz did not expose scorable answer keys. Completion can still be saved, and the stored rating remains `0.0/5` under the current Firebase contract.',
                    )}
                  </ListeningQuizAlert>
                ) : null}

                {saveErrorMessage ? (
                  <ListeningQuizAlert severity="error">
                    {saveErrorMessage}
                  </ListeningQuizAlert>
                ) : null}
              </div>
            </section>
          ) : null}
        </div>
      </LearningLessonShellContent>

      {isFinished ? (
        <LearningLessonShellFooter className="learningListeningQuizFooter">
          <div className="learningListeningQuizFooterInner">
            <LmsButton
              className="learningListeningQuizContinue"
              isLoading={isSaving}
              loadingLabel={t('Saving')}
              onClick={() => void handleSaveAndExit()}
              size="lg"
              trailingIcon={<Save aria-hidden="true" className="size-4" />}
              variant="primary"
            >
              {t('Save and return to learning')}
            </LmsButton>
          </div>
        </LearningLessonShellFooter>
      ) : null}

      <Dialog onOpenChange={setReviewOpen} open={reviewOpen}>
        <DialogContent className="learningListeningQuizReviewDialog">
          <DialogHeader>
            <DialogTitle>{t('Quiz review')}</DialogTitle>
          </DialogHeader>
          <div className="learningListeningQuizReviewList">
            {originalItems.map((item, index) => (
              <div className="learningListeningQuizReviewItem" key={item.id}>
                <div className="learningListeningQuizReviewItemHeader">
                    <h2 className="learningListeningQuizReviewItemTitle">
                      {index + 1}.{' '}
                      {resolveListeningQuizPromptTitle(
                        item,
                        resolveReaderLanguage(i18n.language),
                        index + 1,
                        t('Online Listening Quiz'),
                      )}
                    </h2>
                    {reviewResults[item.id] ? (
                      <ListeningQuizStatusBadge
                        icon={
                          reviewResults[item.id] === 'correct' ? (
                            <CheckCircle2 aria-hidden="true" className="size-3.5" />
                          ) : (
                            <XCircle aria-hidden="true" className="size-3.5" />
                          )
                        }
                        tone={reviewResults[item.id] === 'correct' ? 'success' : 'warning'}
                      >
                        {reviewResults[item.id] === 'correct' ? t('Correct') : t('Incorrect')}
                      </ListeningQuizStatusBadge>
                    ) : null}
                </div>

                  {resolveListeningQuizQuestionTexts(
                    item,
                    resolveReaderLanguage(i18n.language),
                  ).map((question, questionIndex) => (
                    <p className="learningListeningQuizReviewQuestion" key={`${item.id}-review-${questionIndex}`}>
                      {question}
                    </p>
                  ))}
              </div>
            ))}
          </div>
          <DialogFooter className="learningListeningQuizReviewFooter">
            <LmsButton
              leadingIcon={<RotateCcw aria-hidden="true" className="size-4" />}
              onClick={() => {
                setReviewOpen(false)
              }}
              variant="primary"
            >
              {t('Return to quiz')}
            </LmsButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </LearningLessonShell>
  )
}

export function ListeningQuizScreen() {
  useWarmLearningFeedbackAudio()
  const navigate = useNavigate()
  const { t } = useTranslation('book')
  const { state } = useDashboardState()
  const session = state.listeningQuizSession

  if (!session) {
    return (
      <LearningLessonShell className="learningResponsiveScreen learningListeningQuizScreen learningListeningQuizShell">
        <ListeningQuizShellHeader
          onBack={() => {
            navigate(learnerPaths.learning)
          }}
        />
        <LearningLessonShellContent
          className="learningListeningQuizContent"
          pageWrapClassName="learningListeningQuizPageWrap"
          viewportClassName="learningListeningQuizViewport"
        >
          <div className="learningListeningQuizLesson">
            <ListeningQuizAlert
              action={
                <LmsButton
                  leadingIcon={<ArrowLeft aria-hidden="true" className="size-4" />}
                  onClick={() => {
                    navigate(learnerPaths.learning)
                  }}
                  size="sm"
                  variant="outline"
                >
                  {t('Back')}
                </LmsButton>
              }
              className="learningListeningQuizFallback"
              severity="info"
            >
              {t('Start a listening quiz from the learning catalog first.')}
            </ListeningQuizAlert>
          </div>
        </LearningLessonShellContent>
      </LearningLessonShell>
    )
  }

  return (
    <ListeningQuizSessionScreen
      key={`${session.lesson.id}-${session.createdAt}`}
      session={session}
    />
  )
}





